import React, { useCallback, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PropTypes from "prop-types";
import moment from "moment";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import InputFieldText from "Components/Common/InputFieldText";
import {
  getOptionLabel,
  getOptionValue,
  moduleTypes,
  paymentModeOptions,
} from "common/enums";
import Tooltip from "Components/Common/Tooltip";
import DatePicker from "Components/Common/DatePicker";
import SelectField from "Components/Common/SelectField";
import PaymentDetailsEditModal from "./PaymentDetailsEditModal";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import Backdrop from "Components/Common/Backdrop";
import {
  SUPER_ADMIN,
  allAdminRoles,
  powerApplicationsRoles,
} from "common/constant";

import { getImageUrl, isAuthorizedUser } from "utils";
import getFileExtension from "utils/getFileExtension";

import { useLazyDownloadExcelQuery } from "slices/api";
import {
  useDeletePaymentHistoryDetailMutation,
  usePostPaymentHistoryFileMutation,
} from "apis/Payment";

import { PAYMENT_HISTORY } from "tagTypes";

const SUCCESS_STATUS = "Success";

const RECORDS_PER_PAGE = 10;

const validFileFormats = ["xls", "xlsx"];

const initialFilters = {
  searchText: "",
  selectedModuleTypeId: "",
  consumerNumber: "",
  paymentMode: "",
  transactionDate: moment(),
};

const columnProps = { style: { width: "150px", minWidth: "150px" } };

const PaymentHistory = ({ t }) => {
  const resetPageRef = useRef();
  const { roles } = useSelector((state) => state.Login);

  const sm = useMediaQuery("(max-width: 575px)");

  const [documentToPreview, setDocumentToPreview] = useState("");
  const [editModelData, setEditModelData] = useState(null);
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [paymentHistoryIdToDelete, setPaymentHistoryIdToDelete] = useState("");
  const [query, setQuery] = useState({});
  const [
    {
      searchText,
      selectedModuleTypeId,
      consumerNumber,
      transactionDate,
      paymentMode,
    },
    setSelectedFilteredOption,
  ] = useState(initialFilters);

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const [
    uploadPaymentHistoryFile,
    { isLoading: isPaymentHistoryFileUploading },
  ] = usePostPaymentHistoryFileMutation();

  const [deletePaymentDetails, { isLoading: isPaymentDetailDeleting }] =
    useDeletePaymentHistoryDetailMutation();

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/payment-transaction/payment-transaction-history",
      query: {
        ...query,
        transactionDate: encodeURIComponent(
          transactionDate.format("YYYY-MM-DD")
        ),
      },
      tags: [PAYMENT_HISTORY],
    }),
    [query]
  );

  const downloadPaymentTransactionReport = async () => {
    try {
      await downloadExcel({
        endpoint: "/payment-transaction/excel-report",
        sheetName: "Payment History",
        query: {
          ...query,
          transactionDate: encodeURIComponent(
            transactionDate.format("YYYY-MM-DD")
          ),
        },
      });
    } catch (error) {}
  };

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const deleteBtnHandler = async () => {
    try {
      await deletePaymentDetails(paymentHistoryIdToDelete);
      setPaymentHistoryIdToDelete("");
    } catch (error) {}
  };

  const columns = [
    {
      title: "Utility",
      renderCell: ({ utilityId }) =>
        getOptionLabel("utilityOptions", utilityId),
      columnProps,
    },

    {
      title: "Document Date",
      renderCell: ({ documentDate }) =>
        moment(documentDate).format("DD-MM-YYYY  hh:mm:ss A"),
      columnProps,
    },
    {
      title: "Company Code",
      field: "companyCode",
      columnProps,
    },
    {
      title: "Posting Date",
      renderCell: ({ postingDate }) =>
        moment(postingDate).format("DD-MM-YYYY  hh:mm:ss A"),
      columnProps,
    },
    {
      title: "Header Reference",
      field: "headerReference",
      columnProps,
    },
    {
      title: "Doc. Header Text",
      field: "docHeaderText",
      columnProps,
    },
    {
      title: "G/L Account",
      field: "glAccount",
      columnProps,
    },
    {
      title: "Module Type",
      renderCell: ({ moduleType }) => getOptionLabel("moduleTypes", moduleType),
      columnProps: { style: { width: "200px", minWidth: "200px" } },
    },
    {
      title: "Amount",
      renderCell: ({ amount }) => (
        <span className="text-nowrap"> &#8377; {amount}</span>
      ),
      columnProps,
    },
    {
      title: "Value Date",
      renderCell: ({ valueDate }) =>
        moment(valueDate).format("DD-MM-YYYY  hh:mm:ss A"),
      columnProps,
    },
    {
      title: "Check/Transfer No",
      field: "checkTransferNo",
      columnProps: { style: { width: "200px", minWidth: "200px" } },
    },
    {
      title: "Narration",
      field: "narration",
      columnProps,
    },
    {
      title: "Profit Center",
      field: "profitCenter",
      columnProps,
    },
    {
      title: "TDS GL",
      field: "tdsgl",
      columnProps,
    },
    {
      title: "Customer",
      field: "customer",
      columnProps: { style: { width: "200px", minWidth: "200px" } },
    },
    {
      title: "Baseline Date",
      renderCell: ({ baselineDate }) =>
        moment(baselineDate).format("DD-MM-YYYY hh:mm:ss A"),
      columnProps,
    },
    {
      title: "Assignment",
      field: "assignment",
      columnProps,
    },
    {
      title: "Text",
      field: "text",
      columnProps: { style: { width: "200px", minWidth: "200px" } },
    },
    {
      title: "Payment Mode",
      field: "paymentMode",
      columnProps,
    },
    {
      title: "Payment Status",
      renderCell: ({ orderStatus, message }) => (
        <>
          {orderStatus}
          {!!message && (
            <Tooltip title={t(message)}>
              <InfoOutlinedIcon
                className="ms-1 cursor-pointer"
                color="primary"
                fontSize="small"
              />
            </Tooltip>
          )}
        </>
      ),
      columnProps,
    },
    {
      title: "Actions",
      renderCell: ({
        receiptUrl,
        headerReference,
        orderStatus,
        id,
        assignment,
        moduleTypeId,
        baselineDate,
        glAccount,
        paymentMode,
        moduleType,
        tdsYear,
        amount,
        tds,
        gstAmount,
        utilityId,
      }) => (
        <Box sx={{ minWidth: "120px" }}>
          <IconButton
            disabled={!receiptUrl && orderStatus !== SUCCESS_STATUS}
            onClick={() =>
              setDocumentToPreview({
                url: receiptUrl ? getImageUrl(receiptUrl) : "",
                documentTypeName: "Bill",
                documentType: "pdf",
                documentName: `${headerReference}-payment-receipt`,
              })
            }
          >
            <Tooltip title={t("View Bill Receipt")}>
              <RemoveRedEyeIcon
                fontSize="small"
                color={
                  !receiptUrl && orderStatus !== SUCCESS_STATUS
                    ? "disabled"
                    : "primary"
                }
              />
            </Tooltip>
          </IconButton>

          {!!getOptionValue("paymentModeEnum", paymentMode) &&
            isAuthorizedUser(roles, [
              SUPER_ADMIN,
              ...powerApplicationsRoles,
            ]) && (
              <>
                <IconButton
                  onClick={() => {
                    setEditModelData({
                      id,
                      assignment,
                      paymentMode,
                      amount,
                      transactionDate: baselineDate,
                      glAccount,
                      tds,
                      moduleTypeId,
                      moduleType,
                      tdsYear,
                      gstAmount: gstAmount ?? {},
                      isPowerUtility:
                        getOptionValue("utilityOptions", "Power") === utilityId,
                      isOtherUtilitySubscription: [
                        getOptionValue("moduleTypes", "DCS Billing"),
                        getOptionValue("moduleTypes", "Water Billing"),
                        getOptionValue("moduleTypes", "Sewage Billing"),
                        getOptionValue("moduleTypes", "Solid Waste Billing"),
                      ].includes(moduleType),
                    });
                  }}
                >
                  <Tooltip title={t("Edit Payment Details")}>
                    <EditIcon fontSize="small" color="primary" />
                  </Tooltip>
                </IconButton>
                <IconButton onClick={() => setPaymentHistoryIdToDelete(id)}>
                  <Tooltip title={t("Delete Payment Details")}>
                    <DeleteIcon fontSize="small" color="primary" />
                  </Tooltip>
                </IconButton>
              </>
            )}
        </Box>
      ),
    },
  ];

  const moduleTypesOptions = useMemo(
    () => [{ label: "All", value: "" }, ...moduleTypes],
    []
  );

  const handleUploadFile = async (selectedFile) => {
    try {
      const formData = new FormData();
      const fileExtension = getFileExtension(selectedFile.name)?.toLowerCase();
      if (!validFileFormats.includes(fileExtension)) {
        toast.error(
          "Invalid file format, Please upload file with .xsl or .xlsx extension."
        );
      } else {
        formData.append("file", selectedFile);
        await uploadPaymentHistoryFile(formData);
      }
    } catch (err) {}
  };

  const clearBtnHandler = () => {
    handleResetPage();
    setSelectedFilteredOption(initialFilters);
    setQuery({});
  };

  const searchBtnHandler = () => {
    handleResetPage();
    setQuery({
      searchParam: searchText,
      moduleType: selectedModuleTypeId,
      consumerNumber: consumerNumber,
      transactionDate: encodeURIComponent(transactionDate.format("YYYY-MM-DD")),
      isOfflinePayment: paymentMode,
    });
  };

  return (
    <div className="page-content">
      <Backdrop
        open={isExcelFileDownloading || isPaymentHistoryFileUploading}
      />
      <BreadCrumb title={t("Payment History")} pageTitle={t("Payment")} />

      {!!editModelData && (
        <PaymentDetailsEditModal
          editModelData={editModelData}
          handleClose={() => setEditModelData(null)}
        />
      )}

      {!!paymentHistoryIdToDelete && (
        <ConfirmationDialog
          message="Are you sure you want to delete this payment detail?"
          onCancel={() => setPaymentHistoryIdToDelete("")}
          onConfirm={deleteBtnHandler}
          isOpen
          title="Warning"
          variant="warning"
          isLoading={isPaymentDetailDeleting}
        />
      )}

      {documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <Grid container columnSpacing={2} className="mt-1">
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <DatePicker
            slotProps={{
              textField: {
                size: "small",
              },
            }}
            label={t("Select Month and Year")}
            className="w-100"
            views={["month", "year"]}
            value={transactionDate}
            disableFuture
            onChange={(date) =>
              setSelectedFilteredOption((prevState) => ({
                ...prevState,
                transactionDate: date,
              }))
            }
          />
        </Grid>
        <Grid item lg={3} md={4} sm={6} xs={12}>
          <InputFieldText
            label={t("Search Here")}
            className="w-100"
            value={searchText}
            placeholder={t("Search Here")}
            onChange={(e) =>
              setSelectedFilteredOption((prevState) => ({
                ...prevState,
                searchText: e.target.value,
              }))
            }
          />
        </Grid>

        <Grid item lg={3} md={4} sm={6} xs={12}>
          <Autocomplete
            className="w-100 mb-2"
            size="small"
            value={
              moduleTypesOptions?.find(
                (moduleType) => moduleType.value === selectedModuleTypeId
              ) || ""
            }
            disableClearable
            options={moduleTypesOptions}
            renderInput={(params) => (
              <TextField {...params} label={t("Module Type")} />
            )}
            onChange={(_, option) =>
              setSelectedFilteredOption((prevState) => ({
                ...prevState,
                selectedModuleTypeId: option.value,
              }))
            }
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              );
            }}
          />
        </Grid>

        <Grid item lg={3} md={4} sm={6} xs={12}>
          <InputFieldText
            label={t("Consumer Number")}
            className="w-100"
            value={consumerNumber}
            placeholder={t("Enter Consumer Number")}
            onChange={(e) =>
              setSelectedFilteredOption((prevState) => ({
                ...prevState,
                consumerNumber: e.target.value,
              }))
            }
          />
        </Grid>

        <Grid item lg={3} md={4} sm={6} xs={12}>
          <SelectField
            label={t("Payment Mode")}
            value={
              paymentModeOptions.find(({ value }) => paymentMode === value) ??
              ""
            }
            options={paymentModeOptions}
            onChange={(_, { value }) => {
              setSelectedFilteredOption((prevState) => ({
                ...prevState,
                paymentMode: value,
              }));
            }}
          />
        </Grid>

        <Grid item>
          <Button
            variant="outlined"
            onClick={searchBtnHandler}
            className="me-3 mb-2"
          >
            {t("Search")}
          </Button>
          <Button
            variant="outlined"
            onClick={clearBtnHandler}
            className="me-3 mb-2"
          >
            {t("Clear")}
          </Button>
          {isAuthorizedUser(roles, allAdminRoles) && (
            <>
              <Button
                variant="contained"
                className="mb-2 me-2"
                onClick={downloadPaymentTransactionReport}
              >
                {t("Download")}
              </Button>
              <Button
                variant="contained"
                className="mb-2 me-2"
                component="label"
              >
                {t("Upload")}
                <input
                  style={{
                    display: "none",
                  }}
                  accept=".xls, .xlsx"
                  type="file"
                  onChange={async (e) => {
                    await handleUploadFile(e.target.files[0]);
                    e.target.value = "";
                  }}
                />
              </Button>
            </>
          )}
        </Grid>
      </Grid>

      <BaseTable
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={sm ? "calc(100vh - 330px)" : "calc(100vh - 300px)"}
      />
    </div>
  );
};

PaymentHistory.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(PaymentHistory);
